import {
  URL_ACCOUNT_SELECT,
  URL_ANALYTICS_AD_PERFORMANCE,
  URL_BLOG_HIRING_ADVICE_ROOT,
  URL_BLOG_MARKET_INSIGHTS_ROOT,
  URL_CANDIDATES,
  URL_CANDIDATES_ACTIONS,
  URL_CANDIDATES_RECOMMENDED,
  URL_COPY_JOB,
  URL_EDIT_JOB,
  URL_GLOBAL_JOB_DETAILS_EXPIRED,
  URL_GLOBAL_JOB_DETAILS_OPEN,
  URL_LAWS_OF_ATTRACTION,
  URL_PRODUCTS_FIND_A_RECRUITER,
  URL_REFRESH_JOB,
  URL_UNIFIED_TALENT_SEARCH,
  URL_UPGRADE_JOB,
  URL_YOUR_CANDIDATES,
} from '@seek/adv-constants';
import { JobStatus } from '@seek/hirer-job-information';
import type { createUrlResolver } from '@seek/melways-sites';

import type { JOB_APPLICATION_TAB } from './tabs';

const paths = {
  CANDIDATE_MANAGEMENT: URL_CANDIDATES,
  OPTIONAL_LANGUAGE_SEGMENT: '/:language?',
  ACTIONS: URL_CANDIDATES_ACTIONS,
  YOUR_CANDIDATES: URL_YOUR_CANDIDATES,
  RECOMMENDED_CANDIDATES: URL_CANDIDATES_RECOMMENDED,
} as const;

const apiEndpoints = {
  getAttachment: '/api/v2/prospectAttachment/getAttachment',
} as const;

export interface ActionsParams {
  action: 'shortlist' | 'not-suitable';
  positionId: string;
  prospectId: string;
  tracking?: string;
}

interface BaseCandidateParams {
  selected?: string;
  tab?: JOB_APPLICATION_TAB;
  s?: string;
  tracking?: string;
}

export type CandidateParams =
  | (BaseCandidateParams & { jobid: string; id?: never })
  | (BaseCandidateParams & { id: string; jobid?: never });

interface AccountSelectParams {
  returnUrl: string;
}
interface GetAttachmentParams {
  prospectId: number;
  attachmentId: string;
  prefix: string;
}

interface JobAdParams {
  jobId: number;
  jobStatus: JobStatus;
  ref: string;
}
const simpleRouter = {
  accountSelect: (params: AccountSelectParams) => {
    const query = new URLSearchParams({ ...params });
    const queryString = query.toString();
    return `${URL_ACCOUNT_SELECT}${queryString ? `?${queryString}` : ''}`;
  },
  candidates: (params: CandidateParams) => {
    const query = new URLSearchParams(removeNonNullable({ ...params }));
    const queryString = query.toString();
    return `${paths.CANDIDATE_MANAGEMENT}/${
      queryString ? `?${queryString}` : ''
    }`;
  },
  actions: (params: ActionsParams) => {
    const query = new URLSearchParams(removeNonNullable({ ...params }));
    return `${paths.ACTIONS}/?${query.toString()}`;
  },
  yourCandidates: () => paths.YOUR_CANDIDATES,
  getAttachment: ({
    attachmentId,
    prospectId,
    prefix,
  }: GetAttachmentParams) => {
    const params = {
      prospectID: prospectId.toString(),
      attachmentID: attachmentId,
    };
    const queryString = new URLSearchParams(params).toString();
    return `${prefix}${apiEndpoints.getAttachment}?${queryString}`;
  },
  adPerformance: ({
    jobId,
    ref,
  }: {
    jobId: number;
    ref: 'candiman button' | 'candiman popup';
  }) => `${URL_ANALYTICS_AD_PERFORMANCE}/${jobId}?ref=${ref}`,
  recommendedCandidates: (jobId: number) =>
    `${paths.RECOMMENDED_CANDIDATES}?jobId=${jobId}`,
  upgradeJob: ({
    jobId,
    ref,
  }: {
    jobId: number;
    ref:
      | 'candidate-listing-candidate-management'
      | 'pop-up-urgent-hiring-candidate-management'
      | 'pop-up-hard-to-fill-candidate-management'
      | 'placement-survey-candidate-management';
  }): string => `${URL_UPGRADE_JOB}/${jobId}?ref=${ref}`,

  hiringAdvice: ({
    path,
  }: {
    path?:
      | 'article/how-to-write-a-great-job-ad-with-examples'
      | 'article/are-candidates-seeing-your-job-ad-make-these-changes'
      | 'article/losing-candidates-through-the-hiring-process-heres-what-to-do-asia'
      | 'article/kehilangan-kandidat-saat-proses-rekrutmen-inilah-harus-anda-lakukan';
  }) => `${URL_BLOG_HIRING_ADVICE_ROOT}/${path ? path : ''}`,
  marketInsights: ({
    path,
  }: {
    path?:
      | 'article/optimise-your-job-ad-with-unique-market-data'
      | 'talent-attraction-lab/data-lab';
  }) => `${URL_BLOG_MARKET_INSIGHTS_ROOT}/${path ? path : ''}`,
  findRecruiter: () => URL_PRODUCTS_FIND_A_RECRUITER,
  loaWithData: () => `${URL_LAWS_OF_ATTRACTION}/datalab?driver=ALL`,
};

export const createRouter = ({
  urlResolver,
}: {
  urlResolver: ReturnType<typeof createUrlResolver>;
}) => ({
  urlResolver, // For paths that cannot be resolved by this router
  accountSelect: (input: AccountSelectParams) =>
    urlResolver({ path: simpleRouter.accountSelect(input) }),
  candidates: (input: CandidateParams) =>
    urlResolver({ path: simpleRouter.candidates(input) }),
  actions: (input: ActionsParams) =>
    urlResolver({ path: simpleRouter.actions(input) }),
  yourCandidates: () => urlResolver({ path: simpleRouter.yourCandidates() }),
  getAttachment: simpleRouter.getAttachment, // Adcentre does not support language
  adPerformance: ({
    jobId,
    ref,
  }: {
    jobId: number;
    ref: 'candiman button' | 'candiman popup';
  }) =>
    urlResolver({
      path: simpleRouter.adPerformance({
        jobId,
        ref,
      }),
    }),
  viewJobAd: ({ jobStatus, jobId, ref }: JobAdParams) => {
    const basePath =
      jobStatus === JobStatus.EXPIRED
        ? URL_GLOBAL_JOB_DETAILS_EXPIRED
        : URL_GLOBAL_JOB_DETAILS_OPEN;
    return urlResolver({
      product: 'candidate',
      path: `${basePath}/${jobId}?ref=${ref}`,
    });
  },
  editJobAd: ({ jobId }: { jobId: number }) =>
    urlResolver({ path: `${URL_EDIT_JOB}?jobId=${jobId}` }),
  refreshJobAd: ({ jobId }: { jobId: number }) =>
    urlResolver({ path: `${URL_REFRESH_JOB}?jobId=${jobId}` }),
  copyJobAd: ({ jobId }: { jobId: number }) =>
    urlResolver({ path: `${URL_COPY_JOB}&jobId=${jobId}` }),
  talentSearchForJob: ({
    jobId,
    seekAdvertiserId,
  }: {
    jobId: number;
    seekAdvertiserId: string;
  }) =>
    urlResolver({
      path: `${URL_UNIFIED_TALENT_SEARCH}/search/job/${jobId}/profiles?advertiserId=${seekAdvertiserId}`,
    }),
  recommendedCandidates: ({ jobId }: { jobId: number }) =>
    urlResolver({
      path: simpleRouter.recommendedCandidates(jobId),
    }),
  upgradeJob: ({
    jobId,
    ref,
  }: Parameters<(typeof simpleRouter)['upgradeJob']>[0]) =>
    urlResolver({ path: simpleRouter.upgradeJob({ jobId, ref }) }),

  hiringAdvice: ({
    path,
  }: {
    path?:
      | 'article/how-to-write-a-great-job-ad-with-examples'
      | 'article/are-candidates-seeing-your-job-ad-make-these-changes'
      | 'article/losing-candidates-through-the-hiring-process-heres-what-to-do-asia'
      | 'article/kehilangan-kandidat-saat-proses-rekrutmen-inilah-harus-anda-lakukan';
  } = {}) => urlResolver({ path: simpleRouter.hiringAdvice({ path }) }),
  marketInsights: ({
    path,
  }: {
    path?:
      | 'article/optimise-your-job-ad-with-unique-market-data'
      | 'talent-attraction-lab/data-lab';
  } = {}) => urlResolver({ path: simpleRouter.marketInsights({ path }) }),
  findRecruiter: () => urlResolver({ path: simpleRouter.findRecruiter() }),
  loaWithData: () =>
    urlResolver({ product: 'candidate', path: simpleRouter.loaWithData() }),
});

export type Router = ReturnType<typeof createRouter>;

/**
 * Function that takes an object and return a new object without undefined or null key-value
 * Useful to clean up data before putting into new URLSearchParams() to avoid printing out ?param1=undefined&param2=null
 *
 * Example:
 * Input:
 * {
 *   id: 'something',
 *   name: null,
 *   age: undefined
 * }
 *
 * Output:
 * {
 *   id: 'something'
 * }
 */
const removeNonNullable = (
  params: Record<string, string | undefined | null>,
): Record<string, string> => {
  const result: Record<string, string> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      result[key] = value;
    }
  });

  return result;
};

export default paths;
